import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { isPublicRoute } from '@/lib/publicRoutes';

interface TeamMembership {
  teamId: string;
  role: 'super_admin' | 'corporate_owner' | 'admin' | 'manager' | 'member' | 'viewer';
  joinedAt: string;
  status?: string;
}

interface UserSubscription {
  plan: 'personal' | 'professional' | 'business' | 'enterprise';
  status: 'free' | 'active' | 'cancelled' | 'past_due';
  billingCycle?: 'monthly' | 'yearly';
}

interface AINotesCredits {
  balance: number;
  purchased: number;
  used: number;
  isUnlimited: boolean;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  username?: string;
  accountType?: string;
  accountRole?: string;
  corporateAccountId?: string;
  profilePicture?: string;
  hasTeams?: boolean; // Team membership flag
  teams?: TeamMembership[]; // Team memberships array with proper typing
  subscription?: UserSubscription;
  aiNotesCredits?: AINotesCredits;
  // Auth method for delete account flow
  authMethod?: 'google' | 'apple' | 'password';
  hasPassword?: boolean;
  isFirstLogin?: boolean;
}

interface Session {
  user: User;
  access_token: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, username?: string, accountType?: string) => Promise<any>;
  signIn: (emailOrUsername: string, password: string) => Promise<void>;
  signInWithGoogle: (accountType?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔍 [Auth] Initializing authentication...');

      // Detect ?impersonate= query param (admin opens user app in new tab)
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateParam = urlParams.get('impersonate');
      if (impersonateParam) {
        console.log('🔑 [Auth] Impersonation param detected in URL, storing...');
        sessionStorage.setItem('impersonation_token', impersonateParam);
        sessionStorage.setItem('impersonation_active', 'true');
        const impersonateUser = urlParams.get('impersonate_user');
        if (impersonateUser) {
          try { sessionStorage.setItem('impersonation_user', decodeURIComponent(impersonateUser)); } catch {}
        }
        // Clean URL (remove query params)
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Handle impersonation mode — fetch user from API using impersonation token
      const impersonationToken = sessionStorage.getItem('impersonation_token');
      if (impersonationToken) {
        console.log('🔑 [Auth] Impersonation mode detected, fetching impersonated user...');
        try {
          const response = await api.request('/api/auth/me') as any;
          if (response.success && response.user) {
            const impUser: User = {
              id: response.user.id,
              email: response.user.email,
              fullName: response.user.name,
              username: response.user.username,
              accountType: response.user.accountType,
              accountRole: response.user.accountRole,
              corporateAccountId: response.user.corporateAccountId || null,
              profilePicture: response.user.avatar,
              hasTeams: response.user.hasTeams,
              teams: response.user.teams,
              subscription: response.user.subscription || { plan: 'personal', status: 'free' },
              aiNotesCredits: response.user.aiNotesCredits || { balance: 0, purchased: 0, used: 0, isUnlimited: false },
              authMethod: response.user.authMethod,
              hasPassword: response.user.hasPassword,
              isFirstLogin: response.user.isFirstLogin
            };
            setUser(impUser);
            setSession({ user: impUser, access_token: impersonationToken });
            // Store user info for the banner
            sessionStorage.setItem('impersonation_user', JSON.stringify({ name: impUser.fullName, email: impUser.email }));
            console.log('✅ [Auth] Impersonation initialized for:', impUser.email);
          } else {
            console.error('❌ [Auth] Impersonation token invalid, clearing...');
            sessionStorage.removeItem('impersonation_token');
            sessionStorage.removeItem('impersonation_active');
            sessionStorage.removeItem('impersonation_user');
          }
        } catch (error) {
          console.error('❌ [Auth] Impersonation auth failed:', error);
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonation_active');
          sessionStorage.removeItem('impersonation_user');
        }
        setLoading(false);
        return;
      }
      
      // Version check - clear old corrupted data
      const authVersion = localStorage.getItem('auth_version');
      const CURRENT_AUTH_VERSION = '3.0'; // Bumped to 3.0 to clear session-based login corrupted data
      
      console.log('🔍 [Auth] Version check:', { current: authVersion, required: CURRENT_AUTH_VERSION });
      
      if (authVersion !== CURRENT_AUTH_VERSION) {
        console.log('🔄 [Auth] Clearing old auth data (version mismatch)');
        console.log(`   Old version: ${authVersion || 'none'}, New version: ${CURRENT_AUTH_VERSION}`);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.setItem('auth_version', CURRENT_AUTH_VERSION);
        console.log('✅ [Auth] Cleared old data, user must re-login');
        setLoading(false);
        return;
      }
      
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user_data');
      
      console.log('🔍 [Auth] Checking stored credentials:', { 
        hasToken: !!storedToken, 
        hasUser: !!storedUser,
        tokenLength: storedToken?.length || 0,
        userLength: storedUser?.length || 0
      });
      
      if (storedToken && storedUser) {
        try {
          console.log('🔍 [Auth] Parsing stored user data...');
          const userData = JSON.parse(storedUser);
          console.log('🔍 [Auth] Raw user data keys:', Object.keys(userData));
          
          // Sanitize user data to prevent circular references
          const safeUserData = {
            id: userData.id || userData._id,
            email: userData.email,
            fullName: userData.fullName || userData.name,
            username: userData.username,
            accountType: userData.accountType,
            accountRole: userData.accountDetails?.accountRole || userData.accountRole,
            corporateAccountId: userData.corporateAccountId || null,
            profilePicture: userData.profilePicture || userData.avatar,
            hasTeams: userData.hasTeams,
            teams: userData.teams,
            subscription: userData.subscription || { plan: 'personal', status: 'free' },
            aiNotesCredits: userData.aiNotesCredits || { balance: 0, purchased: 0, used: 0, isUnlimited: false },
            isFirstLogin: userData.isFirstLogin
          };
          
          console.log('✅ [Auth] Created safe user data:', { 
            id: safeUserData.id, 
            email: safeUserData.email,
            fullName: safeUserData.fullName,
            accountType: safeUserData.accountType,
            accountRole: safeUserData.accountRole, // DEBUGGING: Show accountRole
            hasAccountRole: !!safeUserData.accountRole // DEBUGGING: Check if it exists
          });
          console.log('🔍 [Auth] Setting user state...');
          setUser(safeUserData);
          console.log('🔍 [Auth] Setting session state...');
          setSession({ user: safeUserData, access_token: storedToken });
          console.log('✅ [Auth] Auth state initialized successfully');
        } catch (error) {
          console.error('❌ [Auth] Failed to parse stored user data:', error);
          // Clear corrupted data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          console.log('🧹 [Auth] Cleared corrupted localStorage data');
        }
        setLoading(false);
      } else if (storedToken && !storedUser) {
        // Token exists but user data is missing - fetch from API
        console.log('⚠️ [Auth] Token exists but user_data missing - fetching from API');
        try {
          const response: any = await api.request('/api/auth/me');
          
          if (response.success && response.user) {
            const newUser: User = {
              id: response.user.id,
              email: response.user.email,
              fullName: response.user.name,
              username: response.user.username,
              accountType: response.user.accountType,
              accountRole: response.user.accountRole,
              corporateAccountId: response.user.corporateAccountId || null,
              profilePicture: response.user.avatar,
              hasTeams: response.user.hasTeams,
              teams: response.user.teams,
              subscription: response.user.subscription || { plan: 'personal', status: 'free' },
              aiNotesCredits: response.user.aiNotesCredits || { balance: 0, purchased: 0, used: 0, isUnlimited: false },
              authMethod: response.user.authMethod,
              hasPassword: response.user.hasPassword,
              isFirstLogin: response.user.isFirstLogin
            };

            console.log('✅ [Auth] Fetched user from API:', {
              email: newUser.email,
              credits: newUser.aiNotesCredits?.balance
            });

            setUser(newUser);
            setSession({ user: newUser, access_token: storedToken });
            localStorage.setItem('user_data', JSON.stringify(newUser));
          } else {
            console.log('❌ [Auth] Failed to fetch user - clearing token');
            localStorage.removeItem('auth_token');
          }
        } catch (error) {
          console.error('❌ [Auth] Error fetching user:', error);
          localStorage.removeItem('auth_token');
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for token expiry events
    const handleTokenExpired = (e?: Event) => {
      console.log('🚫 [Auth] Token expired event received');
      setUser(null);
      setSession(null);

      // On public routes (QR-scanned card, marketing, blog, SEO), clear auth state
      // silently. The user was viewing a page that never needed auth in the first place;
      // ejecting them to /login would destroy their context for no reason. If they later
      // try to reach a private route, the normal auth flow prompts them there.
      if (isPublicRoute()) {
        console.log('ℹ️ [Auth] On public route, suppressing session-expired toast + redirect');
        return;
      }

      const reason = (e as CustomEvent)?.detail?.reason;
      const isDeactivated = reason === 'deactivated';

      toast({
        title: isDeactivated ? 'Account deactivated' : 'Session expired',
        description: isDeactivated
          ? 'Your account has been deactivated by your administrator.'
          : 'Your session has expired. Please login again.',
        variant: 'destructive',
      });

      // Redirect to login page after a brief delay
      setTimeout(() => {
        window.location.href = isDeactivated ? '/login?reason=deactivated' : '/login';
      }, 1500);
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);

    // Handle bfcache (back-forward cache) restoration
    // When user logs out and clicks back button, page may be restored from cache
    // with stale auth state - we need to re-validate
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('📄 [Auth] Page restored from bfcache, re-validating auth state...');
        const token = localStorage.getItem('auth_token');
        if (!token) {
          // User logged out but page was restored from cache
          console.log('🚫 [Auth] No token found after bfcache restore, clearing state');
          setUser(null);
          setSession(null);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [toast]);

  const signUp = async (email: string, password: string, fullName: string, username?: string, accountType: string = 'individual') => {
    try {
      const response = await api.signup(
        email, 
        password, 
        fullName, 
        username || email.split('@')[0],
        accountType
      );

      if (response.success) {
        // Check if email verification is required
        if (response.redirectToVerification) {
          toast({
            title: "Account created!",
            description: response.message || "Please check your email for verification code.",
          });
          // Return response so caller can handle redirect to verification page
          return response;
        }

        // Direct login (no verification needed)
        if (response.profile && response.token) {
          const newUser: User = {
            id: response.profile._id,
            email: response.profile.email,
            fullName: response.profile.name,
            username: response.profile.username,
            authMethod: 'password', // Signup always uses password
            hasPassword: true
          };

          const newSession: Session = {
            user: newUser,
            access_token: response.token,
          };

          // Store auth data
          localStorage.setItem('auth_token', response.token);
          localStorage.setItem('user_data', JSON.stringify(newUser));

          setUser(newUser);
          setSession(newSession);

          toast({
            title: "Account created",
            description: "Welcome to Connectiko!",
          });
        }
      }

      return response;
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signIn = async (emailOrUsername: string, password: string) => {
    try {
      const response = await api.login(emailOrUsername, password);

      if (response.success) {
        const newUser: User = {
          id: response.user.id,
          email: response.user.email,
          fullName: response.user.name,
          username: response.user.username,
          accountType: response.user.accountType,
          accountRole: response.user.accountRole, // FIX: Use direct accountRole
          corporateAccountId: response.user.corporateAccountId || null,
          profilePicture: response.user.avatar,
          hasTeams: response.user.hasTeams, // FIX: Add hasTeams
          teams: response.user.teams, // FIX: Add teams array
          subscription: response.user.subscription || { plan: 'personal', status: 'free' },
          aiNotesCredits: response.user.aiNotesCredits || { balance: 0, purchased: 0, used: 0, isUnlimited: false },
          authMethod: response.user.authMethod as User['authMethod'],
          hasPassword: response.user.hasPassword,
          isFirstLogin: response.user.isFirstLogin
        };

        console.log('🔐 [Auth] Login successful - User object:', {
          email: newUser.email,
          accountType: newUser.accountType,
          accountRole: newUser.accountRole,
          hasTeams: newUser.hasTeams, // FIX: Log hasTeams
          teamsCount: newUser.teams?.length || 0, // FIX: Log teams count
          rawResponse: {
            hasAccountRole: !!response.user.accountRole,
            accountRole: response.user.accountRole,
            hasTeams: response.user.hasTeams,
            teamsCount: response.user.teams?.length || 0
          }
        });

        const newSession: Session = {
          user: newUser,
          access_token: response.token,
        };

        // Store auth data
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user_data', JSON.stringify(newUser));
        localStorage.setItem('auth_version', '3.0'); // Mark as new version
        // Clear stale sessionStorage profile cache so the hook always fetches fresh data on login
        sessionStorage.removeItem('user_profile');
        sessionStorage.removeItem('profile_cache_version');
        // Cache nav-config from login response so enterprise sidebar renders without FOUC
        if (response.navConfig != null) {
          sessionStorage.setItem('corp_nav_config', JSON.stringify(response.navConfig));
        } else {
          sessionStorage.removeItem('corp_nav_config');
        }
        
        console.log('💾 [Auth] Stored to localStorage:', {
          hasToken: !!response.token,
          userData: newUser
        });

        setUser(newUser);
        setSession(newSession);

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Sign in failed",
        description: error.message || "Invalid credentials",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signInWithGoogle = async (accountType: string = 'individual') => {
    try {
      // Initialize Google OAuth with account type
      await api.googleInit(accountType);
      
      // Redirect to Google OAuth URL
      window.location.href = api.getGoogleAuthUrl();
    } catch (error: any) {
      toast({
        title: "Google sign in failed",
        description: error.message || "Failed to initialize Google sign in",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Disconnect all WebSocket connections to prevent reconnection spam
      try {
        const { disconnectCacheListener } = await import('@/lib/cacheEventListener');
        disconnectCacheListener();
      } catch (err) {
        // Cache listener might not be initialized
      }
      
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      // Clear session storage (user profile cache + nav-config cache)
      sessionStorage.removeItem('user_profile');
      sessionStorage.removeItem('corp_nav_config');
      setUser(null);
      setSession(null);
      
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response: any = await api.request('/api/auth/me');
      
      if (response.success && response.user) {
        const updatedUser: User = {
          id: response.user.id,
          email: response.user.email,
          fullName: response.user.name,
          username: response.user.username,
          accountType: response.user.accountType,
          accountRole: response.user.accountRole,
          profilePicture: response.user.avatar,
          hasTeams: response.user.hasTeams,
          teams: response.user.teams,
          subscription: response.user.subscription || { plan: 'personal', status: 'free' },
          aiNotesCredits: response.user.aiNotesCredits || { balance: 0, purchased: 0, used: 0, isUnlimited: false },
          authMethod: response.user.authMethod,
          hasPassword: response.user.hasPassword,
          isFirstLogin: response.user.isFirstLogin
        };

        setUser(updatedUser);
        setSession({ user: updatedUser, access_token: token });
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
